import { useTranslation } from 'react-i18next';

import type { FormInstance } from '@arco-design/web-react/es/Form';

import { NicConfigurationFormItem, isNicConfigurationComplete } from '@/components-biz/NicConfiguration';
import { NicPricingFormItem } from '@/delivery-type-plugin/components/nicPricing/NicPricingFormItem';
import { ResourceConfigModal } from '@/delivery-type-plugin/components/resourceConfig';
import type { NicConfigurationValue } from '@/types/nic-configuration';

export interface NicConfigModalProps {
  parentForm?: FormInstance;
  modalNicsField: string;
  initialValues?: Record<string, unknown>;
  billModelId?: string;
  deploymentTypeId?: string;
  resourceEditMode?: string;
  presetConfigSnapshot?: Record<string, unknown>;
  nicPricingOptions?: Record<string, unknown>;
  onOk: (values: Record<string, unknown>) => void;
  onCancel: () => void;
}

/** 网卡配置弹窗 */
export function NicConfigModal({
  parentForm,
  modalNicsField,
  initialValues,
  billModelId,
  deploymentTypeId,
  resourceEditMode,
  presetConfigSnapshot,
  nicPricingOptions,
  onOk,
  onCancel,
}: NicConfigModalProps) {
  const { t } = useTranslation();
  const useNicPricing = Boolean(nicPricingOptions || billModelId);

  return (
    <ResourceConfigModal
      visible
      title={t('data-center:network.card')}
      resourceCode="Nic"
      vendorDevicePrefix="network.nicPricing"
      vendorDeviceHidden
      initialValues={initialValues}
      validateFields={[modalNicsField]}
      canSubmit={(values) => {
        const nics = values[modalNicsField] as NicConfigurationValue | undefined;
        if (!nics?.length) return false;
        if (useNicPricing) return true;
        return isNicConfigurationComplete(nics);
      }}
      onOk={onOk}
      onCancel={onCancel}
    >
      {(modalForm) =>
        useNicPricing ? (
          <NicPricingFormItem
            form={modalForm}
            nicsField={modalNicsField}
            pricingForm={parentForm ?? modalForm}
            billModelId={billModelId}
            deploymentTypeId={deploymentTypeId}
            resourceEditMode={resourceEditMode}
            presetConfigSnapshot={presetConfigSnapshot}
            {...nicPricingOptions}
          />
        ) : (
          <NicConfigurationFormItem
            field={modalNicsField}
            rules={[
              {
                validator: (value, callback) => {
                  const nics = value as NicConfigurationValue | undefined;
                  if (!nics?.length) return callback(t('data-center:please.select.network.interface.card'));
                  return callback(isNicConfigurationComplete(nics) ? undefined : t('data-center:training.cluster.nic.segment.required'));
                },
              },
            ]}
          />
        )
      }
    </ResourceConfigModal>
  );
}
